import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucidePlus, lucideSave, lucideTrash } from '@ng-icons/lucide';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { toast } from 'ngx-sonner';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { ChipComponent } from '@shared/components/chip/chip.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { EstVerrouilleService } from '@shared/services/est-verrouille.service';
import { Référentiel } from '../../administration/gestion-référentiels/models/référentiel.model';
import { ReferentielStore } from '../../administration/gestion-référentiels/store/referentiel.store';
import { Cartographie } from '../../cartographie/models/cartographie.model';
import { FinParcours } from '../../référentiel-métier-parcours/models/fin-parcours.model';

import { ConfirmDialogComponent } from '../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../shared/components/expansion-panel/expansion-panel.component';
import { FileUploadComponent } from '../../shared/components/file-upload/file-upload.component';
import { ListItem } from '../../shared/components/list-item/list-item.component';
import { ToastrSuccessComponent } from '../../shared/components/toastr/toastr-success.component';
import { RéférentielService } from '../../administration/gestion-référentiels/référentiel.service';

@Component({
  selector: 'app-referentiel-metier-general',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    FileUploadComponent,
    HlmButtonImports,
    HlmInputImports,
    HlmLabelImports,
    HlmFieldImports,
    HlmCardImports,
    HlmIconImports,
    ExpansionPanel,
    ListItem,
    ChipComponent,
    EmptyStateComponent,
  ],
  providers: [provideIcons({ lucideTrash, lucideSave, lucidePlus })],
  templateUrl: './référentiel-métier-général.component.html',
})
export class RéférentielMétierGénéralComponent implements OnDestroy, OnInit {
  readonly #dialog = inject(HlmDialogService);
  readonly #fb = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);
  readonly #localSelectedFileToUpload = signal<File | undefined>(undefined);
  readonly #référentielStore = inject(ReferentielStore);
  readonly #référentielService = inject(RéférentielService);
  readonly #canEditService = inject(CanEditService);
  readonly #estVerrouilleService = inject(EstVerrouilleService);

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = computed(() => this.#référentielStore.selectedEntity());
  readonly roles = input.required<string[]>();

  readonly peutModifier = computed(() =>
    this.#canEditService.check(
      this.referentiel()?.equipeResponsable,
      this.roles(),
      LivrablePrincipal.REFERENTIEL_METIER,
      this.cartographie(),
      this.referentiel(),
    ),
  );

  readonly estVerrouille = computed(() =>
    this.#estVerrouilleService.check(LivrablePrincipal.REFERENTIEL_METIER, this.cartographie(), this.referentiel()),
  );

  readonly action: ExpansionPanelMenuAction<number>[] = [
    {
      icon: 'lucideTrash',
      label: 'Supprimer le persona',
      action: (index: number | undefined) => this.#supprimerFinParcours(index!),
      invisible: () => !this.peutModifier(),
    },
  ];

  readonly diagnosticReadUrl = signal<string | null>(null);

  readonly fileUpload = viewChild<FileUploadComponent>('fileUpload');

  form = this.#fb.group({
    fins: this.#fb.array([]),
  });

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(debounceTime(1000), takeUntilDestroyed(this.#destroyRef), distinctUntilChanged())
      .subscribe(() => this.#submitPersonas());
  }

  get fins(): FormArray {
    return this.form.get('fins') as FormArray;
  }

  constructor() {
    effect(() => {
      const referentiel = this.referentiel();
      if (referentiel) {
        this.setForm(referentiel);
      }
    });
  }

  ngOnDestroy(): void {
    toast.dismiss();
  }

  setForm(referentiel: Référentiel): void {
    if (!this.peutModifier()) {
      this.form.disable({ emitEvent: false });
    }

    const diagnostic = referentiel.diagnostic;
    if (diagnostic) {
      const url = decodeURIComponent(diagnostic); // this should be an absolute path to blob in azure...
      this.diagnosticReadUrl.set(url);
    }

    if (referentiel.finsParcours) {
      this.fins.clear({ emitEvent: false });
      referentiel.finsParcours.forEach((fin) => this.ajoutNouvelleFin(fin, false));
    }
  }

  ajoutNouvelleFin(fin?: FinParcours, emitEvent?: boolean) {
    const nouvelleFinForm = this.#fb.group({
      id: [fin?.id],
      nom: [
        fin?.nom ?? `Persona ${(this.fins.controls.length + 1).toString().padStart(2, '0')}`,
        {
          validators: [Validators.required, Validators.maxLength(30)],
          updateOn: 'blur',
        },
      ],
      description: [fin?.description, { updateOn: 'blur' }],
    });
    this.fins.push(nouvelleFinForm, { emitEvent: emitEvent ?? true });
  }

  #submitPersonas() {
    const referentiel = this.referentiel();
    if (!referentiel) return;

    this.#référentielStore.modifier({
      id: referentiel.id,
      data: {
        finsParcours: this.fins.value,
        diagnostic: this.diagnosticReadUrl(),
      },
    });
  }

  #supprimerFinParcours(index: number) {
    const fin = this.fins.at(index).value;

    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: `Êtes-vous sûr de vouloir supprimer ${fin.nom} ?`,
        contenu: `Vous êtes sur le point de supprimer <b>${fin.nom}</b>. Cette action est définitive et ne peut pas être annulée.`,
      },
    });
    dialogRef.closed$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((result: boolean) => result),
      )
      .subscribe(async () => this.fins.removeAt(index));
  }

  async uploadDiagnostic(file: File | undefined) {
    const referentiel = this.referentiel();
    if (!referentiel) return;

    this.#localSelectedFileToUpload.set(file);
    if (!file) this.diagnosticReadUrl.set('');

    const toastrId = uuidv7();

    let diagnosticPath = null;
    if (this.#localSelectedFileToUpload()) {
      const uploadUrl = await this.#getUploadUrl();
      if (!uploadUrl) return;
      diagnosticPath = this.#extractPath(uploadUrl);
      this.diagnosticReadUrl.set(uploadUrl);
      await this.fileUpload()?.upload(uploadUrl);
    }

    this.#référentielStore.modifier({
      id: referentiel.id,
      data: { diagnostic: diagnosticPath },
      next: () => {
        this.#référentielStore.selectionnerParId(referentiel.id);
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Modification du référentiel réussie',
            id: toastrId,
          },
          id: toastrId,
        });
      },
    });
  }

  async #getUploadUrl(): Promise<string> {
    const referentiel = this.referentiel();
    if (!referentiel) return '';
    return await this.#référentielService.getUploadUrl(referentiel.id, this.#localSelectedFileToUpload()!.name);
  }

  #extractPath(url: string) {
    const idx = url.lastIndexOf('?');
    const parts = idx < 0 ? url.split('/') : url.split('?')[0].split('/');
    url = '/' + parts.slice(parts[3] === 'devstoreaccount1' ? 4 : 3).join('/');
    url = decodeURIComponent(url);
    return url;
  }
}
